import type { Device } from '@/types/app';
import type { Component } from '@/types/component';
import type { MobileAppEnvironment, MobileAppTrackingStatus } from '@/types/mobileApp';
import type { PaymentConfig, PaymentConfigV2 } from '@/types/payment';
import type { AppState } from 'lib/contexts/AppState';
import { AppStateProvider } from 'lib/contexts/AppState';
import type { MobileAppState } from 'lib/contexts/MobileAppState';
import { MobileAppStateProvider } from 'lib/contexts/MobileAppState';

import { useStable } from '@/hooks/useStable';
import { AbTestId, AbTestVariantId } from '@/types/ocelotConfig/utils';
import { ComponentsStateProvider } from 'lib/contexts/ComponentsState';
import { useRouter } from 'next/router';

export interface StandaloneStateProvidersProps {
  device: Device;
  pageData: any;
  appState?: Partial<AppState>;
  mobileAppState?: Partial<MobileAppState>;
  abTestVariants?: Record<AbTestId, AbTestVariantId>;
  paymentConfig?: PaymentConfig | PaymentConfigV2;
}

export const StandaloneStateProviders: Component<StandaloneStateProvidersProps> = ({ children, ...pageProps }) => {
  const { query, pathname } = useRouter();

  const { device, pageData, appState, mobileAppState, paymentConfig, abTestVariants } = useStable(pageProps);

  const { type, data, meta } = pageData ?? {};
  const { id, isNativeAd, accessLevel, purchaseTag, pageType: metaPageType, isVevArticle } = meta ?? {};
  const { publishedUrl, category, tags } = data ?? {};

  const baseAppState: AppState = useStable({
    device,
    type,
    category,
    publishedUrl,
    tags,
    pageId: id,
    isNativeAd,
    isVevArticle,
    accessLevel,
    purchaseTag,
    metaPageType,
    gamesUrls: ['/korsord', '/korsord-och-spel'],
    isPreviewPage: pathname.startsWith('/_preview'),
    isFrontPage: type === 'front',
    isArticlePage: type === 'article',
    isDesktop: device === 'desktop',
    isMobile: device === 'mobile',
    recommendedArticles: [],
    scrollArticles: [],
    paymentConfig,
    abTestVariants,
  });

  const baseMobileAppState: MobileAppState = useStable({
    isMobileApp: Boolean(query.allerapp),
    appVersion: query['app-version'] as string,
    buildVersion: query['build-version'] as string,
    environment: query.environment as MobileAppEnvironment,
    trackingStatus: query['tracking-status'] as MobileAppTrackingStatus,
  });

  return (
    <AppStateProvider {...baseAppState} {...appState}>
      <MobileAppStateProvider {...baseMobileAppState} {...mobileAppState}>
        <ComponentsStateProvider>{children}</ComponentsStateProvider>
      </MobileAppStateProvider>
    </AppStateProvider>
  );
};
